import { Alert, Button, Descriptions, Space, Typography } from 'antd';
import { useSubscription } from '../../../../../../../../Hooks/TT_ELD/Companies';

const { Text } = Typography;

// e.g. "15 USD", "15 USD / month", "With IFTA · 20 USD / month"
export const priceLabel = (price: any) => {
  const amount = `${(price.unit_amount ?? 0) / 100} ${price.currency?.toUpperCase() || ''}`.trim();
  const interval = price.recurring?.interval ? ` / ${price.recurring.interval}` : '';
  return price.nickname ? `${price.nickname} · ${amount}${interval}` : `${amount}${interval}`;
};

// A company can keep a price ID without a subscription; that price is meaningless then
export const hasSubscription = (company: any) => !!company?.subscriptionId;

// The company only stores the price ID; the subscription has the actual amount. Pick the item
// on the company's plan (a legal subscription also has a processing-fee item).
export const useCurrentPrice = (company: any, companyId: string) => {
  const { data: subscriptionInfo } = useSubscription(company?.subscriptionId, companyId);
  const subscriptionItems: any[] = subscriptionInfo?.data?.subscription?.items?.data ?? [];
  return subscriptionItems.find((item) => item?.price?.id === company?.priceId)?.price;
};

// Price amount with a copy-to-clipboard icon for its Stripe price ID
const PriceWithCopy = ({ price, priceId }: { price?: any; priceId?: string | null }) => {
  if (!priceId) return <>-</>;
  return (
    <Text copyable={{ text: priceId, tooltips: ['Copy price ID', 'Price ID copied'] }}>
      {price ? priceLabel(price) : priceId}
    </Text>
  );
};

interface Props {
  companyId: string;
  company: any;
  canManage: boolean;
  // The last migration switched the company but its price was never set in the migration modal
  pricePending: boolean;
  onSetPrice(): void;
}

// Read-only: the price is chosen once, while migrating. Other price changes go through the Stripe tab.
const SubscriptionSummary = ({ companyId, company, canManage, pricePending, onSetPrice }: Props) => {
  const currentPrice = useCurrentPrice(company, companyId);
  const subscribed = hasSubscription(company);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
        <Descriptions.Item label="Current price">
          <PriceWithCopy price={currentPrice} priceId={subscribed ? company?.priceId : null} />
        </Descriptions.Item>
        <Descriptions.Item label="Quantity">{company?.quantity ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="Stripe status">{company?.stripeStatus || '-'}</Descriptions.Item>
      </Descriptions>

      {subscribed && pricePending && canManage && (
        <Alert
          type="warning"
          showIcon
          message="Price not set"
          description="The company was migrated, but its subscription price was not set during the migration."
          action={
            <Button size="small" type="primary" onClick={onSetPrice}>
              Set price
            </Button>
          }
        />
      )}

      <Text type="secondary">
        {subscribed
          ?'The price is chosen while migrating. To change it without a migration, use the Stripe tab.'
          : 'This company has no subscription.'}
      </Text>
    </Space>
  );
};

export default SubscriptionSummary;
